import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { RiSearchLine } from 'react-icons/ri'

interface EmployeeSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (selectedEmployees: number[]) => void
  employeeOptions: { label: string; value: number }[]
  selectedEmployees: number[]
}

export function EmployeeSelectionModal({
  isOpen,
  onClose,
  onConfirm,
  employeeOptions,
  selectedEmployees,
}: EmployeeSelectionModalProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [tempSelectedEmployees, setTempSelectedEmployees] = useState<number[]>([])

  useEffect(() => {
    if (isOpen) {
      setTempSelectedEmployees(selectedEmployees)
      setSearchTerm('')
    }
  }, [isOpen, selectedEmployees])

  const filteredEmployees = employeeOptions.filter((employee) =>
    employee.label.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const handleToggleEmployee = (employeeId: number) => {
    setTempSelectedEmployees((prev) =>
      prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId],
    )
  }

  const handleSelectAll = () => {
    if (tempSelectedEmployees.length === filteredEmployees.length) {
      setTempSelectedEmployees([])
    } else {
      setTempSelectedEmployees(filteredEmployees.map((employee) => employee.value))
    }
  }

  const handleConfirm = () => {
    onConfirm(tempSelectedEmployees)
    onClose()
  }

  const handleCancel = () => {
    setTempSelectedEmployees(selectedEmployees)
    onClose()
  }

  const isAllSelected = filteredEmployees.length > 0 && tempSelectedEmployees.length === filteredEmployees.length
  const isSomeSelected = tempSelectedEmployees.length > 0 && tempSelectedEmployees.length < filteredEmployees.length

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white" {...({} as any)}>
        <ModalHeader>
          <Text fontSize="lg" fontWeight="bold">
            Selecionar Funcionários
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Campo de busca */}
            <Box>
              <Text fontSize="sm" color="gray.400" mb={2}>
                Buscar funcionário
              </Text>
              <Input
                placeholder="Digite o nome do funcionário..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg="gray.700"
                color="white"
                borderColor="gray.600"
                _hover={{ borderColor: 'pink.400' }}
                _focus={{ borderColor: 'pink.400', boxShadow: 'none' }}
                leftIcon={<RiSearchLine />}
                {...({} as any)}
              />
            </Box>

            {/* Selecionar todos */}
            {filteredEmployees.length > 0 && (
              <HStack
                p={2}
                bg="gray.700"
                borderRadius="md"
                cursor="pointer"
                onClick={handleSelectAll}
                _hover={{ bg: 'gray.600' }}
                {...({} as any)}>
                <Checkbox
                  isChecked={isAllSelected}
                  isIndeterminate={isSomeSelected}
                  onChange={handleSelectAll}
                  colorScheme="pink"
                  {...({} as any)}
                />
                <Text fontSize="sm" fontWeight="medium">
                  {isAllSelected ? 'Desmarcar todos' : 'Selecionar todos'}
                </Text>
              </HStack>
            )}

            {/* Lista de funcionários */}
            <Box maxHeight="300px" overflowY="auto">
              <VStack spacing={0} align="stretch">
                {filteredEmployees.length === 0 ? (
                  <Text color="gray.400" textAlign="center" py={4}>
                    {searchTerm ? 'Nenhum funcionário encontrado' : 'Nenhum funcionário disponível'}
                  </Text>
                ) : (
                  filteredEmployees.map((employee) => (
                    <HStack
                      key={employee.value}
                      p={3}
                      _hover={{ bg: 'gray.600' }}
                      cursor="pointer"
                      onClick={() => handleToggleEmployee(employee.value)}
                      borderRadius="md"
                      {...({} as any)}>
                      <Checkbox
                        isChecked={tempSelectedEmployees.includes(employee.value)}
                        onChange={() => handleToggleEmployee(employee.value)}
                        colorScheme="pink"
                        {...({} as any)}
                      />
                      <Text color="white" fontSize="sm">
                        {employee.label}
                      </Text>
                    </HStack>
                  ))
                )}
              </VStack>
            </Box>

            {/* Contador de selecionados */}
            {tempSelectedEmployees.length > 0 && (
              <Text fontSize="sm" color="pink.400" textAlign="center">
                {tempSelectedEmployees.length} funcionário(s) selecionado(s)
              </Text>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Flex gap={2} width="100%">
            <Button variant="ghost" onClick={handleCancel} flex={1}>
              Cancelar
            </Button>
            <Button colorScheme="pink" onClick={handleConfirm} flex={1}>
              OK
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
